import React, { useState, useEffect, useRef } from 'react';
import { STATUS } from './routineConstants';
import { markRepeats, difficultyFor } from '../../utils/routineDifficulty';
import './routineRepeat.css';

// One routine: its skills in order, total difficulty and notes, plus coach
// actions when canEdit. canSubmit is the gymnast, who can edit and delete
// the routines they entered themselves. Routines the gymnast entered show
// that a coach needs to check them, with an Approve button for coaches.
// A routine added with no signal (unsent) can be edited or deleted, but other
// actions wait until it has reached the server. A coach can also send a
// gymnast's routine back with a note; it stays with the gymnast until they change it.
const GymnastRoutineCard = ({ routine, canEdit, canSubmit, isFirst, isLast, onEdit, onCustomise, onCopy, onDelete, onMove, onApprove, onSendBack, onDiscard }) => {
  const status = STATUS[routine.status] || STATUS.WORKING_ON;
  const linked = Boolean(routine.levelRoutine);
  const sentBack = routine.pendingVerification && Boolean(routine.sentBackAt);
  const waiting = routine.pendingVerification && !sentBack;
  const approved = routine.submittedByGymnast && !routine.pendingVerification && Boolean(routine.verifiedAt);

  return (
    <div className="gymnast-routine">
      <div className="gymnast-routine__header">
        <strong className="gymnast-routine__name">{routine.name}</strong>
        <span className={`badge ${status.badge}`}>{status.label}</span>
        {linked && (
          <span className="badge badge-primary" title="Skills come from the level routine and stay in sync">
            Linked: Level {routine.levelRoutine.level.identifier}
          </span>
        )}
        {waiting && (
          <span className="badge badge-warning" title="Entered by the gymnast. A coach needs to check it before it can be used in a competition.">
            Needs a coach to check
          </span>
        )}
        {sentBack && (
          <span className="badge badge-danger" title="A coach sent this back. It comes back to be checked when it's changed.">
            Sent back by {canEdit ? 'a coach' : 'your coach'}
          </span>
        )}
        {approved && !canEdit && <span className="badge badge-success">Approved by your coach</span>}
        {routine.pending && (
          <span className="badge badge-secondary" title="Saved on this phone. It will be sent when there's signal.">Waiting to send</span>
        )}
        {routine.failed && (
          <span className="badge badge-danger">Not saved: {routine.failed}</span>
        )}
        {routine.failed && onDiscard && (
          <button type="button" className="btn btn-sm btn-outline" onClick={onDiscard}>Discard</button>
        )}
        {routine.totalDifficulty > 0 && (
          <span className="gymnast-routine__dd">DD {routine.totalDifficulty.toFixed(1)}</span>
        )}
        {canEdit && waiting && !routine.unsent && (
          <span className="gymnast-routine__approve">
            {onSendBack && <button type="button" className="btn btn-sm btn-outline" onClick={onSendBack}>Send back</button>}
            <button type="button" className="btn btn-sm btn-success" onClick={onApprove}>Approve</button>
          </span>
        )}
        {!canEdit && canSubmit && routine.submittedByGymnast && (
          <span className="gymnast-routine__own-actions">
            <button type="button" className="btn btn-sm btn-outline" onClick={onEdit} aria-label={`Edit ${routine.name}`}>Edit</button>
            <button type="button" className="btn btn-sm btn-outline" onClick={onDelete} aria-label={`Delete ${routine.name}`}>Delete</button>
          </span>
        )}
        {canEdit && (
          <RoutineActionsMenu
            routine={routine}
            linked={linked}
            unsent={Boolean(routine.unsent)}
            isFirst={isFirst}
            isLast={isLast}
            onEdit={onEdit}
            onCustomise={onCustomise}
            onCopy={onCopy}
            onDelete={onDelete}
            onMove={onMove}
          />
        )}
      </div>

      {routine.pendingVerification && routine.reviewNote && (
        <p className={`gymnast-routine__review-note${sentBack ? '' : ' gymnast-routine__review-note--earlier'}`}>
          <strong>{sentBack ? 'What to change: ' : 'Coach asked for: '}</strong>{routine.reviewNote}
        </p>
      )}

      {routine.orphaned ? (
        <p className="gymnast-routine__orphaned">
          Level routine removed. Delete this routine, or start a personal routine in its place.
        </p>
      ) : routine.skills.length > 0 ? (
        <ol className="gymnast-routine__skills">
          {markRepeats(routine.skills).map(skill => { const skillDD = difficultyFor(skill, routine.apparatus); return (
            <li key={skill.id} className={[skill.isImplicit && 'is-implicit', skill.isRepeat && 'is-repeat'].filter(Boolean).join(' ') || undefined}>
              <span>
                {skill.name}
                {skill.archivedAt && <span className="text-muted"> (archived)</span>}
                {skill.isImplicit && <span className="routine-legacy-tag" title="Typed in before routines used the skills library. A club admin can swap it for a library skill.">Not in library</span>}
                {skill.isRepeat && <span className="routine-repeat-tag" title="Repeated move: scores no difficulty">Repeat</span>}
              </span>
              <span className="gymnast-routine__skill-meta">
                {skill.figNotation && <code>{skill.figNotation}</code>}
                {skillDD != null && <span>{skill.isRepeat ? '0.0' : skillDD.toFixed(1)}</span>}
              </span>
            </li>
          ); })}
        </ol>
      ) : (
        <p className="text-muted">No skills yet.</p>
      )}

      {routine.notes && <p className="gymnast-routine__notes">{routine.notes}</p>}

    </div>
  );
};

// "…" menu holding the coach actions, so the card stays uncluttered. Closes on
// selection, outside click or Escape.
const RoutineActionsMenu = ({ routine, linked, unsent, isFirst, isLast, onEdit, onCustomise, onCopy, onDelete, onMove }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const item = (label, action, { disabled = false, danger = false } = {}) => (
    <button
      type="button"
      role="menuitem"
      className={`routine-menu__item${danger ? ' routine-menu__item--danger' : ''}`}
      disabled={disabled}
      onClick={() => { setOpen(false); action(); }}
    >
      {label}
    </button>
  );

  return (
    <div className="routine-menu" ref={ref}>
      <button
        type="button"
        className="routine-menu__toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for ${routine.name}`}
        onClick={() => setOpen(o => !o)}
      >
        ⋯
      </button>
      {open && (
        <div className="routine-menu__list" role="menu">
          {routine.orphaned ? item('Start personal routine', onEdit) : item('Edit', onEdit)}
          {linked && item('Customise', onCustomise, { disabled: unsent })}
          {!routine.orphaned && item('Copy to gymnasts…', onCopy, { disabled: unsent })}
          {item('Move up', () => onMove(-1), { disabled: isFirst || unsent })}
          {item('Move down', () => onMove(1), { disabled: isLast || unsent })}
          {item('Delete', onDelete, { danger: true })}
        </div>
      )}
    </div>
  );
};

export default GymnastRoutineCard;
